// @ts-nocheck
import React from 'react';
import type { VariantProps } from '@gluestack-ui/utils/nativewind-utils';
import { textStyle } from './styles';

type ITextProps = React.ComponentProps<'span'> &
  VariantProps<typeof textStyle> & {
// Cross-platform parity: accept the React Native-only `numberOfLines` prop
    // on web too, so callers that use the `truncate + numberOfLines={1}` pattern
    // don't need `@ts-expect-error` or casts. Translated below into a CSS
    // line-clamp so it actually clamps on web instead of being swallowed.
    numberOfLines?: number;
    // Cross-platform parity for Android/iOS font-scaling caps. Swallowed on web
    // (no system font scaling there); kept in the type so callers don't need casts.
    allowFontScaling?: boolean;
    maxFontSizeMultiplier?: number;
  };

/**
 * Translates the RN `numberOfLines` prop into the CSS line-clamp treatment.
 * `numberOfLines` is a runtime-computed value (caller-supplied line count),
 * not one of the closed design-token classes, so it has to be an inline
 * style rather than a Tailwind class.
 * @param numberOfLines - Number of lines to clamp to, or undefined for no clamp.
 * @returns A style object implementing the clamp, or undefined when unset.
 */
function lineClampStyle(
  numberOfLines: number | undefined
): React.CSSProperties | undefined {
  if (!numberOfLines) return undefined;
  return {
    display: '-webkit-box',
    WebkitLineClamp: numberOfLines,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  };
}

const Text = React.forwardRef<React.ComponentRef<'span'>, ITextProps>(
  function Text(
    {
      className,
      isTruncated,
      bold,
      underline,
      strikeThrough,
      size = 'md',
      sub,
      italic,
      highlight,
      numberOfLines,
      style,
      ...props
    }: { className?: string } & ITextProps,
    ref
  ) {
    return (
      <span
        className={textStyle({
          isTruncated: isTruncated as boolean,
          bold: bold as boolean,
          underline: underline as boolean,
          strikeThrough: strikeThrough as boolean,
          size,
          sub: sub as boolean,
          italic: italic as boolean,
          highlight: highlight as boolean,
          class: className,
        })}
        // design-system: dynamic-style waiver — data-driven value (numberOfLines line-clamp count)
        style={{ ...lineClampStyle(numberOfLines), ...style }}
        {...props}
        ref={ref}
      />
    );
  }
);

Text.displayName = 'Text';

export { Text };
